import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import apiService from '../../services/ApiService';
import type { ConnectStatusData } from '../../types/event';
import PageLoader from '../ui/PageLoader';
import Footer from '../Footer';
import Button from '../ui/Button';
import PageBackLink from '../ui/PageBackLink';
import { PageHeader } from '../ui/PageHeader';

const OrganizerPaymentsPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const connectReturn = searchParams.get('connect');
  const [data, setData] = useState<ConnectStatusData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const status = await apiService.getConnectStatus();
      setData(status);
      if (connectReturn === 'return' || connectReturn === 'refresh') {
        try {
          await apiService.refreshConnectOnboarding();
          const refreshed = await apiService.getConnectStatus();
          setData(refreshed);
        } catch {
          /* sync may fail if account not created yet */
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load payout settings');
    } finally {
      setLoading(false);
    }
  }, [connectReturn]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleStartOnboarding = async () => {
    try {
      setBusy(true);
      setError(null);
      const { url } = await apiService.startConnectOnboarding();
      window.location.href = url;
    } catch (err: any) {
      setError(err.message || 'Could not start payout setup');
    } finally {
      setBusy(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setBusy(true);
      setError(null);
      await apiService.refreshConnectOnboarding();
      setData(await apiService.getConnectStatus());
    } catch (err: any) {
      setError(err.message || 'Could not refresh status');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <>
        <PageLoader message="Loading payout settings…" />
        <Footer />
      </>
    );
  }

  const flags = data?.flags || {};
  const eligibility = data?.eligibility;
  const account = eligibility?.account;
  const connectEnabled = flags.stripe_connect_enabled;
  const payoutsEnabled = flags.organizer_payouts_enabled;
  const paidEntryEnabled = flags.paid_vehicle_entry_enabled;
  const ready = eligibility?.eligible;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col">
      <div className="flex-1 max-w-2xl mx-auto w-full px-4 sm:px-6 py-8">
        <PageBackLink label="Back to Organizer" to="/organizer" className="mb-4" />
        <PageHeader
          eyebrow="Organizer"
          title="Payout setup"
          description="Connect your payout account before activating paid vehicle entry. Bilt never stores your bank details."
          className="mb-6"
        />

        {connectReturn ? (
          <div className="mb-4 p-3 rounded-xl border border-emerald-800/50 bg-emerald-950/30 text-sm text-emerald-200">
            {connectReturn === 'return'
              ? 'Welcome back. We refreshed your payout status below.'
              : 'Your session expired. Start setup again if needed.'}
          </div>
        ) : null}

        {error ? (
          <div className="alert-error mb-4 text-sm" role="alert">
            {error}
          </div>
        ) : null}

        <div className="space-y-4">
          <section className="card p-5 space-y-3">
            <h2 className="heading-card text-sm">Platform readiness</h2>
            <ul className="text-sm text-muted space-y-2">
              <li className="flex justify-between gap-4">
                <span>Payout onboarding</span>
                <span className={connectEnabled ? 'text-emerald-400' : 'text-gray-500'}>
                  {connectEnabled ? 'Available' : 'Not enabled yet'}
                </span>
              </li>
              <li className="flex justify-between gap-4">
                <span>Paid vehicle entry</span>
                <span className={paidEntryEnabled ? 'text-emerald-400' : 'text-gray-500'}>
                  {paidEntryEnabled ? 'Available' : 'Not enabled yet'}
                </span>
              </li>
              <li className="flex justify-between gap-4">
                <span>Automatic payouts</span>
                <span className={payoutsEnabled ? 'text-emerald-400' : 'text-gray-500'}>
                  {payoutsEnabled ? 'Enabled' : 'Disabled'}
                </span>
              </li>
            </ul>
          </section>

          <section className="card p-5 space-y-4">
            <h2 className="heading-card text-sm">Your account</h2>
            {!connectEnabled ? (
              <p className="text-sm text-muted leading-relaxed">
                Paid events are not available on this environment yet. Check back when your organizer account is enabled for payouts.
              </p>
            ) : ready ? (
              <p className="text-sm text-emerald-300 leading-relaxed">
                Your payout account is ready. You can activate paid entry on eligible events.
              </p>
            ) : account ? (
              <div className="text-sm text-muted space-y-2 leading-relaxed">
                <p>Onboarding status: {account.onboarding_status.replace(/_/g, ' ')}</p>
                {account.disabled_reason ? (
                  <p className="text-amber-300">Action needed: {account.disabled_reason}</p>
                ) : null}
                <p>Complete any remaining steps to accept entry payments.</p>
              </div>
            ) : (
              <p className="text-sm text-muted leading-relaxed">
                Set up payouts to receive vehicle entry fees directly. Setup is handled securely outside Bilt.
              </p>
            )}

            <div className="flex flex-wrap gap-3">
              {connectEnabled && !ready ? (
                <Button variant="primary" loading={busy} onClick={() => void handleStartOnboarding()} className="min-h-[44px]">
                  {account ? 'Continue setup' : 'Start payout setup'}
                </Button>
              ) : null}
              {connectEnabled && account ? (
                <Button variant="secondary" loading={busy} onClick={() => void handleRefresh()} className="min-h-[44px]">
                  Refresh status
                </Button>
              ) : null}
            </div>
          </section>

          {!payoutsEnabled && connectEnabled ? (
            <p className="text-xs text-gray-500 leading-relaxed">
              Paid entry cannot go live until automatic payouts are enabled platform-wide. You can still complete onboarding now.
            </p>
          ) : null}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default OrganizerPaymentsPage;
